import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { getWhatsAppUrl } from '../utils/whatsapp';
import { useLanguage } from '../context/LanguageContext';
import { Waves, Route, ShieldCheck, MapPin, MessageCircle, Star } from 'lucide-react';
const TOURS = [{
  id: 1,
  duration: 30,
  price: 85,
  image: 'https://cdn.magicpatterns.com/uploads/4XKMRyLeB5F2M3fR9D6xKs/WhatsApp_Image_2026-06-02_at_1.22.23_p._m..jpg',
  accentColor: '#00D4FF'
}, {
  id: 2,
  duration: 45,
  price: 110,
  image: 'https://cdn.magicpatterns.com/uploads/mK68AfdUZT2XenmpL6Vfn7/WhatsApp_Image_2026-06-02_at_1.22.21_p._m..jpg',
  accentColor: '#006994'
}, {
  id: 3,
  duration: 60,
  price: 130,
  image: 'https://cdn.magicpatterns.com/uploads/tGwkQhUGVQitGo4nTxtT99/WhatsApp_Image_2026-06-02_at_1.22.20_p._m._(3).jpg',
  accentColor: '#004d73'
}, {
  id: 4,
  duration: 90,
  price: 160,
  image: 'https://cdn.magicpatterns.com/uploads/b81PBEaRbyorqXpqVXvxRZ/WhatsApp_Image_2026-06-02_at_1.22.20_p._m._(2).jpg',
  accentColor: '#00CED1'
}, {
  id: 5,
  duration: null,
  price: 200,
  image: 'https://cdn.magicpatterns.com/uploads/fCKENewGKEgcVy8e357fcY/WhatsApp_Image_2026-06-02_at_1.22.20_p._m._(1).jpg',
  accentColor: '#FFD700',
  featured: true
}];
export const JetSkiSection = () => {
  const {
    t
  } = useLanguage();
  const handleWhatsApp = () => {
    const message = t('wa.jetski');
    window.open(getWhatsAppUrl(message), '_blank');
  };
  const FEATURES = [{
    icon: Waves,
    text: t('jetski.tour.f1')
  }, {
    icon: Route,
    text: t('jetski.tour.f2')
  }, {
    icon: ShieldCheck,
    text: t('jetski.tour.f3')
  }, {
    icon: MapPin,
    text: t('jetski.tour.f4')
  }];
  return <section id="motos" className="relative overflow-hidden" data-id="element-354">
      {/* HEADER SECTION - Navy with wave pattern */}
      <div className="relative bg-[#0A1F44] text-white py-12 sm:py-16 md:py-20 lg:py-24" data-id="element-355">
        {/* Subtle wave pattern overlay */}
        <div className="absolute inset-0 opacity-10" data-id="element-356">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" data-id="element-357">
            <defs data-id="element-358">
              <pattern id="wave-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse" data-id="element-359">
                <path d="M0 50 Q 25 30, 50 50 T 100 50" stroke="currentColor" strokeWidth="1" fill="none" className="text-[#00D4FF]" data-id="element-360" />
                <path d="M0 70 Q 25 50, 50 70 T 100 70" stroke="currentColor" strokeWidth="1" fill="none" className="text-[#00D4FF]" data-id="element-361" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#wave-pattern)" data-id="element-362" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-363">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-center space-y-6 sm:space-y-8" data-id="element-364">
            {/* Badge */}
            <div className="inline-flex items-center justify-center" data-id="element-365">
              <div className="bg-[#00D4FF] text-white font-black uppercase text-xs sm:text-sm px-4 sm:px-6 py-2 sm:py-2.5 rounded-full tracking-wider" data-id="element-366">
                {t('jetski.tour.badge')}
              </div>
            </div>

            {/* Main Title */}
            <div data-id="element-367">
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black uppercase tracking-tight leading-[0.9] mb-4 whitespace-pre-line" data-id="element-368">
                {t('jetski.tour.title')}
              </h2>
              <p className="text-lg sm:text-xl md:text-2xl font-medium text-[#00D4FF] tracking-wide" data-id="element-369">
                {t('jetski.tour.subtitle')}
              </p>
            </div>

            {/* Features Bar */}
            <div className="pt-8 sm:pt-12" data-id="element-370">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-5xl mx-auto" data-id="element-371">
                {FEATURES.map((feature, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: idx * 0.1,
                duration: 0.5
              }} className="flex flex-col items-center text-center space-y-2 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10" data-id="element-372">
                    <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#00D4FF]" data-id="element-373" />
                    <span className="text-xs sm:text-sm font-medium text-white/90 leading-tight" data-id="element-374">
                      {feature.text}
                    </span>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* TOUR CARDS SECTION - Light background */}
      <div className="bg-slate-50 py-12 sm:py-16 md:py-20" data-id="element-375">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-376">
          {/* Top row - 3 cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8" data-id="element-377">
            {TOURS.slice(0, 3).map((tour, idx) => <TourCard key={tour.id} tour={tour} index={idx} data-id="element-378" />)}
          </div>

          {/* Bottom row - 2 cards centered */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto" data-id="element-379">
            {TOURS.slice(3, 5).map((tour, idx) => <TourCard key={tour.id} tour={tour} index={idx + 3} data-id="element-380" />)}
          </div>
        </div>
      </div>

      {/* BOTTOM CTA BLOCK */}
      <div className="bg-slate-50 pb-12 sm:pb-16 md:pb-20" data-id="element-381">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-382">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative bg-gradient-to-br from-[#0A1F44] via-[#006994] to-[#00CED1] rounded-3xl p-8 sm:p-12 md:p-16 text-center overflow-hidden" data-id="element-383">
            {/* Subtle wave decoration */}
            <div className="absolute inset-0 opacity-5" data-id="element-384">
              <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" data-id="element-385">
                <defs data-id="element-386">
                  <pattern id="cta-waves" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse" data-id="element-387">
                    <path d="M0 60 Q 30 40, 60 60 T 120 60" stroke="white" strokeWidth="2" fill="none" data-id="element-388" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#cta-waves)" data-id="element-389" />
              </svg>
            </div>

            <div className="relative space-y-6" data-id="element-390">
              <div data-id="element-391">
                <h3 className="text-3xl sm:text-4xl md:text-5xl font-black uppercase text-white mb-3 tracking-tight" data-id="element-392">
                  {t('jetski.cta.title')}
                </h3>
                <p className="text-xl sm:text-2xl font-bold text-[#00D4FF] tracking-wide" data-id="element-393">
                  {t('jetski.cta.subtitle')}
                </p>
              </div>

              <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed" data-id="element-394">
                {t('jetski.cta.desc')}
              </p>

              <motion.button onClick={handleWhatsApp} whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.98
            }} className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebd5a] text-white font-black uppercase tracking-wider px-8 py-5 rounded-full shadow-2xl transition-colors text-sm sm:text-base" data-id="element-395">
                <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-396" />
                <span data-id="element-397">{t('jetski.cta.btn')}</span>
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};
const TourCard = ({
  tour,
  index
}: {
  tour: (typeof TOURS)[0];
  index: number;
}) => {
  const {
    t
  } = useLanguage();
  const name = t(`jetski.tour.${tour.id}.name`);
  const desc = t(`jetski.tour.${tour.id}.desc`);
  const includes = tour.id === 5 ? t('jetski.tour.5.includes') : null;
  return <motion.div initial={{
    opacity: 0,
    y: 40
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    delay: index * 0.1,
    duration: 0.6
  }} whileHover={{
    y: -8
  }} className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transition-shadow hover:shadow-2xl flex flex-col ${tour.featured ? 'ring-2 ring-[#FFD700]/40' : ''}`} data-id="element-398">
      {/* Featured badge */}
      {tour.featured && <div className="absolute top-4 right-4 z-10 bg-[#FFD700] text-[#0A1F44] font-black uppercase text-xs px-3 py-1.5 rounded-full flex items-center gap-1 shadow-lg" data-id="element-399">
          <Star className="w-3 h-3 fill-current" data-id="element-400" />
          {t('jetski.card.premium')}
        </div>}

      {/* TOP HALF - Image with location overlay */}
      <div className="relative aspect-[4/3] overflow-hidden" data-id="element-401">
        <img src={tour.image} alt={`Excursión en moto acuática ${name}`} loading={index === 0 ? 'eager' : 'lazy'} decoding="async" fetchPriority={index === 0 ? 'high' : 'auto'} className="w-full h-full object-cover object-center transition-transform duration-700 hover:scale-105" data-id="element-402" />

        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-403" />

        {/* Location name with ribbon */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5" data-id="element-404">
          <div className="relative" data-id="element-405">
            {/* Ribbon background */}
            <div className="absolute inset-0 -left-2 -right-2 bg-[#00D4FF]/20 backdrop-blur-sm transform -skew-x-3" style={{
            top: '-8px',
            bottom: '-8px'
          }} data-id="element-406" />
            {/* Text */}
            <h3 className="relative text-2xl md:text-3xl font-black uppercase text-white tracking-[0.08em] drop-shadow-lg text-center" data-id="element-407">
              {name}
            </h3>
          </div>
        </div>
      </div>

      {/* BOTTOM HALF - Gradient with info */}
      <div className="bg-gradient-to-br from-[#006994] to-[#00CED1] p-5 sm:p-6 flex flex-col gap-4 flex-1" data-id="element-408">
        {/* Duration and Price bubbles */}
        <div className="flex justify-center items-center gap-4" data-id="element-409">
          {/* Duration bubble */}
          <div className="flex flex-col items-center justify-center w-24 h-24 bg-white rounded-full shadow-lg" data-id="element-410">
            {tour.duration ? <>
                <span className="text-3xl font-black text-[#0A1F44] leading-none" data-id="element-411">
                  {tour.duration}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#0A1F44]/70 mt-1" data-id="element-412">
                  {t('jetski.card.min')}
                </span>
              </> : <span className="text-xs font-black uppercase text-[#0A1F44] text-center leading-tight px-2" data-id="element-413">
                {t('jetski.card.from')}
              </span>}
          </div>

          {/* Price bubble */}
          <div className="flex flex-col items-center justify-center w-24 h-24 bg-white rounded-full shadow-lg" data-id="element-414">
            <span className={`font-black text-[#0A1F44] leading-none ${tour.price >= 100 ? 'text-2xl' : 'text-3xl'}`} data-id="element-415">
              {tour.duration ? `${tour.price}€` : `${tour.price}€+`}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#0A1F44]/70 mt-1" data-id="element-416">
              {t('jetski.card.price')}
            </span>
          </div>
        </div>

        {/* Includes tag */}
        {includes && <div className="flex justify-center" data-id="element-417">
            <div className="inline-flex items-center gap-1.5 bg-[#FFD700] text-[#0A1F44] font-black uppercase text-[10px] sm:text-xs px-3 py-1.5 rounded-full text-center leading-tight" data-id="element-418">
              {includes}
            </div>
          </div>}

        {/* Description — fixed height for symmetric cards */}
        <p className="text-sm text-white/90 leading-relaxed text-center min-h-[3.5rem] flex items-center justify-center px-2 mt-auto" data-id="element-419">
          {desc}
        </p>
      </div>
    </motion.div>;
};